import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/index';
import { createPageUrl } from '@/utils';
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";
import ImageUploader from '@/components/ImageUploader.jsx';
import ImageMarkup from '@/components/ImageMarkup.jsx';
import LightParameters from '@/components/LightParameters.jsx';
import PricingForm from '@/components/PricingForm.jsx';
import JumpPlanner from '@/components/JumpPlanner.jsx';
import InstallPlan from '@/components/InstallPlan.jsx';
import CustomerQuote from '@/components/CustomerQuote.jsx';
import QuickStats from '@/components/QuickStats.jsx';
import MaterialsList from '@/components/MaterialsList.jsx';
import ProfitCalculator from '@/components/ProfitCalculator.jsx';
import StreetViewLoader from '@/components/StreetViewLoader.jsx';
import { Textarea } from "@/components/ui/textarea.jsx";
import {
  ArrowLeft, Save, Sparkles, Camera, Pencil, Settings,
  DollarSign, Cable, FileText, Loader2, FileEdit, Briefcase
} from 'lucide-react';

export default function ProjectEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('markup');
  const [localProject, setLocalProject] = useState(null);
  const [calculatedCost, setCalculatedCost] = useState(0);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.entities.Project.get(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (project) {
      setLocalProject(prev => {
        const updated = { ...project };
        if (!project.customer_name || project.customer_name === '') {
          updated.customer_name = project.name;
        }
        return updated;
      });
    }
  }, [project]);

  const updateMutation = useMutation({
    mutationFn: (data) => api.entities.Project.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['project', id]);
      queryClient.invalidateQueries(['projects']);
    },
  });

  const handleSave = async () => {
    if (!localProject) return;
    await updateMutation.mutateAsync(localProject);
  };

  const updateProject = (updates) => {
    setLocalProject({ ...localProject, ...updates });
  };

  if (isLoading || !localProject) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={createPageUrl('Projects')}>
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <img 
                src="/SNO-LightingLogo.png" 
                alt="SNO-Lighting Logo" 
                className="h-8 w-auto hidden sm:block"
              />
              <div>
                <h1 className="text-lg font-bold text-slate-900">{localProject.name}</h1>
                <p className="text-xs text-slate-500">{localProject.address}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select
                value={localProject.status || 'draft'}
                onValueChange={(value) => updateProject({ status: value })}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="installed">Installed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Project
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-5">
            <TabsTrigger value="markup" className="flex items-center gap-2">
              <Pencil className="w-4 h-4" />
              <span className="hidden sm:inline">Image & Markup</span>
            </TabsTrigger>
            <TabsTrigger value="parameters" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Parameters</span>
            </TabsTrigger>
            <TabsTrigger value="connections" className="flex items-center gap-2">
              <Cable className="w-4 h-4" />
              <span className="hidden sm:inline">Connections</span>
            </TabsTrigger>
            <TabsTrigger value="plan" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Install Plan</span>
            </TabsTrigger>
            <TabsTrigger value="quote" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Quote</span>
            </TabsTrigger>
            <TabsTrigger value="business" className="flex items-center gap-2 md:col-span-1 col-span-3">
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">Business</span>
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="markup" className="space-y-6">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  {!localProject.image_url ? (
                    <>
                      <StreetViewLoader
                        address={localProject.address}
                        onImageLoaded={(url, address) => {
                          updateProject({ 
                            image_url: url,
                            address: address 
                          });
                        }}
                      />
                      
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                        <h2 className="text-xl font-bold mb-4">Or Upload Photo Manually</h2>
                        <ImageUploader
                          currentImage={localProject.image_url}
                          onImageUploaded={(url) => updateProject({ image_url: url })}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold">House Photo & Markup</h2>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateProject({ image_url: null })}
                        >
                          Change Photo
                        </Button>
                      </div>
                      <ImageMarkup
                        imageUrl={localProject.image_url}
                        sections={localProject.sections || []}
                        onSectionsChange={(sections) => updateProject({ sections })}
                        pixelsPerFoot={localProject.pixels_per_foot || 10}
                      />
                    </div>
                  )}
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="flex items-center gap-2 mb-4">
                      <FileEdit className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-slate-900">Customer Info</h3>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">Customer Name</Label>
                        <Input
                          value={localProject.customer_name || ''}
                          onChange={(e) => updateProject({ customer_name: e.target.value })}
                          placeholder="John Smith"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Phone</Label>
                        <Input
                          value={localProject.customer_phone || ''}
                          onChange={(e) => updateProject({ customer_phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Email</Label>
                        <Input
                          type="email"
                          value={localProject.customer_email || ''}
                          onChange={(e) => updateProject({ customer_email: e.target.value })}
                          placeholder="john@example.com"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="flex items-center gap-2 mb-4">
                      <FileEdit className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-slate-900">Project Notes</h3>
                    </div>
                    <Textarea
                      value={localProject.notes || ''}
                      onChange={(e) => updateProject({ notes: e.target.value })}
                      placeholder="Add any notes about this project..."
                      className="min-h-[120px] resize-none"
                    />
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <QuickStats
                      sections={localProject.sections || []}
                      parameters={localProject.parameters || {}}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="parameters" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-amber-600" />
                    Light Parameters
                  </h2>
                  <LightParameters
                    parameters={localProject.parameters || {}}
                    onChange={(parameters) => updateProject({ parameters })}
                  />
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                    Pricing
                  </h2>
                  <PricingForm
                    pricing={localProject.pricing || {}}
                    onChange={(pricing) => updateProject({ pricing })}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="connections" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Cable className="w-5 h-5 text-amber-600" />
                    Section Connections
                  </h2>
                  <JumpPlanner
                    sections={localProject.sections || []}
                    jumps={localProject.jumps || []}
                    onJumpsChange={(jumps) => updateProject({ jumps })}
                    lightType={localProject.parameters?.light_type}
                    voltage={localProject.parameters?.voltage}
                  />
                </div>
                
                {localProject.image_url && (localProject.sections || []).length > 0 && (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Pencil className="w-5 h-5 text-amber-600" />
                      Marked Sections
                    </h2>
                    <ImageMarkup
                      imageUrl={localProject.image_url}
                      sections={localProject.sections || []}
                      onSectionsChange={() => {}}
                      pixelsPerFoot={localProject.pixels_per_foot || 10}
                      readOnly={true}
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="plan" className="space-y-6">
              <InstallPlan project={localProject} />
            </TabsContent>

            <TabsContent value="quote" className="space-y-6">
              <CustomerQuote 
                project={localProject}
                onTotalChange={(total) => {
                  if (localProject.quoted_price !== total) {
                    updateProject({ quoted_price: total });
                  }
                }}
              />
            </TabsContent>

            <TabsContent value="business" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <MaterialsList 
                  sections={localProject.sections || []}
                  jumps={localProject.jumps || []}
                  parameters={localProject.parameters || {}}
                  onCostCalculated={setCalculatedCost}
                />
                <ProfitCalculator
                  totalCost={calculatedCost}
                  quotedPrice={localProject.quoted_price || 0}
                  onChange={(price) => updateProject({ quoted_price: price })}
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
